import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { DashList, Formula, LearnArticleLink, LearnSeriesNav } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.plan-viabilidad-hosteleria-primeros-pasos.${field}`

const SOURCES = [
    {
        href: 'https://www.infohoreca.com/noticias/20260522/apertura-restaurantes-consejos-errores-linkers',
        labelKey: 'sources.1',
        fallback:
            'InfoHoreca, "El 60% de los restaurantes cierra antes de un año: los errores ocultos que están arruinando la hostelería española", 22 de mayo de 2026, con datos de la consultora Linkers (regla del alquiler y coste de personal).',
    },
    {
        href: 'https://www.hosteleriamadrid.com/blog/fiscal/gastos-hosteleria/',
        labelKey: 'sources.2',
        fallback:
            'Hostelería de Madrid, "Gastos de un negocio de hostelería", diciembre de 2025: coincide en que el alquiler no debería superar el 10 % de la facturación anual.',
    },
] as const

export function PlanViabilidadHosteleriaPrimerosPasosArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        {
            id: 'local-enamorado',
            label: t(k('toc.1'), '1. El local que te enamoró (y que firmaste sin hacer números)'),
        },
        {
            id: 'error-caro',
            label: t(k('toc.2'), '2. El error más caro: elegir el local antes de tener tu plan de viabilidad'),
        },
        {
            id: 'concepto',
            label: t(k('toc.3'), '3. Paso 1: el concepto y el ticket medio, en una frase'),
        },
        {
            id: 'mercado',
            label: t(k('toc.4'), '4. Paso 2: cuánta gente vende ya lo mismo que tú'),
        },
        {
            id: 'costes',
            label: t(k('toc.5'), '5. Paso 3: separa tus costes en fijos y variables'),
        },
        {
            id: 'equilibrio',
            label: t(k('toc.6'), '6. Paso 4: calcula tu punto de equilibrio'),
        },
        {
            id: 'ejemplo',
            label: t(k('toc.7'), '7. Un ejemplo con números: el bar de tapas de 60 m²'),
        },
        {
            id: 'cinco-calculos',
            label: t(k('toc.8'), '8. Los cinco cálculos antes de ver un solo local'),
        },
        {
            id: 'antes-copiar',
            label: t(k('toc.9'), '9. Antes de copiar estos números en tu propio plan'),
        },
    ]

    const fixedCosts = [
        t(k('list.fijos.1'), 'Alquiler.'),
        t(k('list.fijos.2'), 'Personal fijo (nóminas y Seguridad Social).'),
        t(k('list.fijos.3'), 'Suministros: luz, agua, gas.'),
        t(k('list.fijos.4'), 'Seguros y gestoría.'),
    ]

    const variableCosts = [
        t(k('list.variables.1'), 'Materia prima.'),
        t(k('list.variables.2'), 'Personal eventual o refuerzos.'),
        t(k('list.variables.3'), 'Comisiones del TPV.'),
    ]

    const fiveCalcs = [
        t(k('list.cinco.1'), 'Escribe tu concepto en una frase con ticket medio incluido.'),
        t(k('list.cinco.2'), 'Cuenta los negocios parecidos al tuyo en tu zona y anota su ticket medio.'),
        t(k('list.cinco.3'), 'Lista tus costes fijos estimados, uno por uno, sin redondear a la baja.'),
        t(k('list.cinco.4'), 'Calcula tu punto de equilibrio con la fórmula de arriba.'),
        t(
            k('list.cinco.5'),
            'Divide el alquiler que estás mirando entre ese punto de equilibrio. Si pasa del 10 %, busca otro local o baja tus costes fijos.',
        ),
    ]

    const exampleRows = [
        {
            concept: t(k('table.row.1.concept'), 'Alquiler'),
            amount: t(k('table.row.1.amount'), '900 €/mes'),
        },
        {
            concept: t(k('table.row.2.concept'), 'Personal fijo, suministros, seguros y gestoría'),
            amount: t(k('table.row.2.amount'), '4.700 €/mes'),
        },
        {
            concept: t(k('table.row.3.concept'), 'Costes fijos totales'),
            amount: t(k('table.row.3.amount'), '5.600 €/mes'),
            strong: true,
        },
        {
            concept: t(k('table.row.4.concept'), 'Costes variables (materia prima + eventual)'),
            amount: t(k('table.row.4.amount'), '40 % de la venta'),
        },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Segundo capítulo de nuestra guía «De idea a negocio»: concepto, mercado, costes y punto de equilibrio antes de firmar un local.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Bar vacío antes de abrir con sillas encima de las mesas, y en la barra un bloc con números y una calculadora junto a las llaves del local',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                <span className={articleUi.em}>
                                    {t(
                                        k('lead'),
                                        'El coste fijo que más restaurantes hunde no es la materia prima, es el alquiler mal calculado: la regla del sector dice que no debería superar el 8-10 % de la facturación prevista, y muchos firman el contrato antes de saber si esa facturación es realista.',
                                    )}
                                </span>{' '}
                                {t(
                                    k('lead.rest'),
                                    'Antes de buscar local hace falta un plan de viabilidad con cuatro números propios: concepto, mercado, costes y punto de equilibrio. Este es el segundo paso de la guía, y el primero con calculadora.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(
                                    k('seriesNote.before'),
                                    'Publicado el 8 de septiembre de 2026. Segundo capítulo de la guía «De idea a negocio». Si todavía no has hecho el ',
                                )}
                                <LearnArticleLink slug='de-idea-a-negocio-paso-1-viabilidad'>
                                    {t(k('seriesNote.link'), 'primer paso')}
                                </LearnArticleLink>
                                {t(k('seriesNote.after'), ', empieza por ahí.')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='local-enamorado' className={articleUi.h2}>
                        {t(k('h2.localEnamorado'), 'El local que te enamoró (y que firmaste sin hacer números)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.localEnamorado.1'),
                                'Lo viste un martes. Buena calle, buen precio, el dueño con prisa por cerrar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.localEnamorado.2'), 'Llamaste esa misma tarde. Firmaste esa misma semana.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.localEnamorado.3'),
                                'Y solo después, con el contrato ya firmado, te sentaste a calcular cuánto tenías que vender cada día para pagarlo. El número no encajaba, y ya no había vuelta atrás.',
                            )}
                        </p>
                    </div>

                    <h2 id='error-caro' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(
                            k('h2.errorCaro'),
                            'El error más caro: elegir el local antes de tener tu plan de viabilidad',
                        )}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.errorCaro.1'),
                                'No es una excepción, es el patrón más repetido del sector. El diagnóstico habitual coincide: el fallo no suele estar en la cocina ni en la ubicación en sí, sino en decisiones tomadas en el orden equivocado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.errorCaro.2'),
                                'El local se busca el cuarto paso, no el primero. Antes van el concepto, el mercado y los números que dicen si ese concepto aguanta en ese mercado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.errorCaro.3before'), 'Es el mismo patrón que contábamos en el ')}
                            <LearnArticleLink slug='de-idea-a-negocio-paso-1-viabilidad'>
                                {t(k('p.errorCaro.3link'), 'primer capítulo')}
                            </LearnArticleLink>
                            {t(
                                k('p.errorCaro.3after'),
                                ': seis de cada diez restaurantes no llegan al año, casi siempre porque estos números se calculan después de firmar, no antes.',
                            )}
                        </p>
                    </div>

                    <h2 id='concepto' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.concepto'), 'Paso 1: el concepto y el ticket medio, en una frase')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.concepto.1'),
                                'Si no puedes resumir tu negocio en una frase con un ticket medio dentro, todavía no tienes un concepto: tienes una idea.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.concepto.2'),
                                '"Bar de tapas de toda la vida, ticket medio 12 €" es un concepto. "Un sitio con buen rollo" no lo es.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.concepto.3'),
                                'El ticket medio no te lo inventas: lo sacas de mirar lo que cobran los negocios parecidos al tuyo en tu zona, no en la ciudad que más te guste de Instagram.',
                            )}
                        </p>
                    </div>

                    <h2 id='mercado' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.mercado'), 'Paso 2: cuánta gente vende ya lo mismo que tú')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.mercado.1'),
                                'Este paso ya lo apuntamos en el primer capítulo: conocer el mercado es haber contado, no intuido.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.mercado.2'),
                                'Cuenta los negocios parecidos al tuyo a diez minutos andando. Anota su ticket medio, sus horarios y cuánta gente entra un martes normal, no un sábado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.mercado.3'),
                                'Si esa cuenta te sale a cero competidores, sospecha: puede que no haya demanda, no que hayas encontrado un hueco de oro.',
                            )}
                        </p>
                    </div>

                    <h2 id='costes' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.costes'), 'Paso 3: separa tus costes en fijos y variables')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.costes.1'), 'Fijos son los que pagas exista o no exista un cliente:')}
                        </p>
                        <DashList items={fixedCosts} />
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.costes.2'), 'Variables son los que suben y bajan con la venta:')}
                        </p>
                        <DashList items={variableCosts} />
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.costes.3Em'),
                                    'Un aviso del propio sector: las plantillas mal planificadas disparan el coste de personal por encima del 45 % de la facturación',
                                )}
                            </span>
                            {t(
                                k('p.costes.3'),
                                ', y eso es lo que convierte un local lleno en un negocio que no gana dinero. La línea entre fijo y variable no siempre está clara (un camarero de refuerzo los sábados es variable, el mismo camarero con contrato indefinido es fijo), y precisamente por eso merece la pena escribirla, no calcularla de memoria.',
                            )}
                        </p>
                    </div>

                    <h2 id='equilibrio' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.equilibrio'), 'Paso 4: calcula tu punto de equilibrio')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.equilibrio.1'),
                                'Con esos dos números ya puedes calcular cuánto tienes que facturar al mes para no perder dinero.',
                            )}
                        </p>
                        <Formula variant='tight'>
                            {t(
                                k('formula.equilibrio'),
                                'Punto de equilibrio = costes fijos ÷ (1 − % de costes variables sobre la venta).',
                            )}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.equilibrio.2'),
                                'Ese resultado es tu primer número real. Todo lo que factures por encima es beneficio. Todo lo que factures por debajo lo pones tú.',
                            )}
                        </p>
                    </div>

                    <h2 id='ejemplo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.ejemplo'), 'Un ejemplo con números: el bar de tapas de 60 m²')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.pNote, articleUi.pGapMd)}>
                            {t(
                                k('p.ejemplo.note'),
                                'Estimación propia sobre un bar de tapas de 60 m², no es un dato de mercado.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.caption'), 'Costes del ejemplo de bar de tapas de 60 m²')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.col.concept'), 'Concepto')}</th>
                                    <th scope='col'>{t(k('table.col.amount'), 'Importe')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {exampleRows.map(row => (
                                    <tr key={row.concept}>
                                        <th scope='row'>
                                            {row.strong ? (
                                                <span className={articleUi.em}>{row.concept}</span>
                                            ) : (
                                                row.concept
                                            )}
                                        </th>
                                        <td>
                                            {row.strong ? (
                                                <span className={articleUi.em}>{row.amount}</span>
                                            ) : (
                                                row.amount
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(k('p.ejemplo.1Em'), 'Punto de equilibrio: 5.600 € ÷ (1 − 0,40) = 9.333 € al mes.')}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.ejemplo.2before'), 'Con un ticket medio de 12 €, son unas ')}
                            <span className={articleUi.em}>
                                {t(k('p.ejemplo.2Em'), '778 comandas al mes, 26 al día')}
                            </span>
                            {t(
                                k('p.ejemplo.2after'),
                                '. Y el alquiler de 900 € sobre esos 9.333 € es un 9,6 %, dentro de la regla del 8-10 %.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.ejemplo.3'),
                                'Si ese mismo local costara 1.400 € en lugar de 900 €, el punto de equilibrio subiría a 10.167 €. Esos 500 € más de alquiler no son 500 € más que vender: al dividirlos entre el margen, son 833 € más de facturación necesaria cada mes, para siempre. Y ese alquiler ya no cumple la regla: se va al 13,8 % de la facturación que necesitas.',
                            )}
                        </p>
                    </div>

                    <h2 id='cinco-calculos' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.cincoCalculos'), 'Los cinco cálculos antes de ver un solo local')}
                    </h2>
                    <ol className={articleUi.numListGap2xl}>
                        {fiveCalcs.map(item => (
                            <li key={item} className={articleUi.numItem}>
                                {item}
                            </li>
                        ))}
                    </ol>

                    <h2 id='antes-copiar' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.antesCopiar'), 'Antes de copiar estos números en tu propio plan')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.antesCopiar.1'),
                                'El 40 % de costes variables y los 4.700 € de costes fijos de nuestro ejemplo son una estimación propia para un bar pequeño: tu ciudad, tu plantilla y tu concepto cambian esos números, a veces mucho. Un mismo concepto en Madrid capital y en una capital de provincia puede tener el doble de alquiler y un ticket medio muy parecido, y eso cambia el punto de equilibrio entero.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.antesCopiar.2'),
                                'Tampoco hay una cifra única de coste fijo "normal": varía tanto entre un bar de barrio y un restaurante de menú que cualquier referencia genérica es solo un punto de partida. La regla del alquiler al 8-10 % sí aparece de forma consistente entre varias fuentes del sector, pero el resto de porcentajes cambia según a quién le preguntes.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.antesCopiar.3'),
                                'Nos queda el paso siguiente sin resolver: una vez sabes cuánto necesitas facturar, ¿cómo sabes si tu zona puede darte esas comandas? Eso es la búsqueda de local con datos, y es el próximo capítulo de esta guía.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('closing.signoff'), 'Hasta la próxima noticia que pueda ser de ayuda para el sector.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={articleUi.link}
                                >
                                    {t(k(source.labelKey), source.fallback)}
                                </a>
                            </li>
                        ))}
                        <li>
                            {t(
                                k('sources.3'),
                                'Elaboración propia de Tipi: fórmula del punto de equilibrio y ejemplo con números.',
                            )}
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
